import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { oapi, fmtMoney } from '@/lib/officialsApi';
import { Field } from './PayBits';

const MODES = [['fill', 'Only fill roles that have no fee yet', 'Keeps every fee the league already has.'],
  ['overwrite', "Overwrite the league's fees", 'Replaces them with the template. Roles not in the template go back to the default.']];

const ModePicker = ({ mode, setMode }) => (
  <div className="space-y-2" data-testid="apply-mode">
    {MODES.map(([k, l, sub]) => (
      <button key={k} type="button" onClick={() => setMode(k)} data-testid={`apply-mode-${k}`}
        className={`w-full text-left rounded-xl border-2 px-3 py-2 transition-colors ${mode === k ? 'border-[#00247D] bg-[#00247D]/5' : 'border-slate-200 hover:border-slate-300'}`}>
        <div className="text-sm font-semibold text-slate-800">{l}</div><div className="text-xs text-slate-500">{sub}</div>
      </button>
    ))}
  </div>
);

// target: { template_id?, event_ids? }
export const ApplyTemplateDialog = ({ target, templates, orgId, events, rates, currency, onClose, onApplied, onCreateTemplate }) => {
  const [tid, setTid] = useState('');
  const [sel, setSel] = useState(new Set());
  const [mode, setMode] = useState('');
  const [withBudget, setWithBudget] = useState(true);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!target) return;
    setTid(target.template_id || (templates.length === 1 ? templates[0].id : ''));
    setSel(new Set(target.event_ids || []));
    setMode('');
    setWithBudget(true);
  }, [target, templates]);
  const own = useMemo(() => {
    const c = {};
    rates.filter(r => r.scope === 'event').forEach(r => { c[r.ref] = (c[r.ref] || 0) + 1; });
    return c;
  }, [rates]);
  const t = templates.find(x => x.id === tid);
  const conflicts = [...sel].filter(id => own[id]).length;
  const effMode = conflicts ? mode : 'fill';
  const toggle = (id) => setSel(p => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const submit = async () => {
    setBusy(true);
    try {
      const r = await oapi(`/officials/payments/rate-templates/${tid}/apply`, { method: 'POST', orgId,
        body: { event_ids: [...sel], mode: effMode, include_budget: !!(withBudget && t.budget_minor !== null) } });
      toast.success(`Applied to ${r.events} league${r.events === 1 ? '' : 's'} · ${r.changed} fee${r.changed === 1 ? '' : 's'} changed${r.budgets_set ? ` · ${r.budgets_set} budget${r.budgets_set === 1 ? '' : 's'} set` : ''}`);
      onApplied(r);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!target} onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="apply-template-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#00247D]">Apply a rate template</DialogTitle>
          <DialogDescription>Copy a saved fee setup onto leagues or tournaments. Approved and paid fees never change; unpaid ones are recalculated.</DialogDescription>
        </DialogHeader>
        {!templates.length ? (
          <div className="rounded-xl border border-dashed p-5 text-center" data-testid="apply-no-templates">
            <p className="text-sm text-slate-600">You don't have any templates yet.</p>
            <Button size="sm" className="mt-3 bg-[#00247D] hover:bg-[#001A5C]" onClick={onCreateTemplate} data-testid="apply-create-template"><Plus className="w-4 h-4 mr-1.5" />Create a template</Button>
          </div>
        ) : (
          <div className="space-y-4">
            <Field label="Template">
              <Select value={tid || undefined} onValueChange={setTid}>
                <SelectTrigger data-testid="apply-template-select"><SelectValue placeholder="Choose a template" /></SelectTrigger>
                <SelectContent>{templates.map(x => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label={`Apply to (${sel.size} selected)`}>
              <div className="max-h-48 overflow-y-auto rounded-xl border divide-y">
                {events.map(e => (
                  <label key={e.id} className="flex items-center gap-2.5 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50" data-testid={`apply-target-${e.id}`}>
                    <Checkbox checked={sel.has(e.id)} onCheckedChange={() => toggle(e.id)} data-testid={`apply-target-check-${e.id}`} />
                    <span className="flex-1 font-medium text-slate-800">{e.name}</span>
                    {own[e.id] ? <span className="text-[11px] font-bold text-amber-800 bg-amber-100 rounded-full px-2 py-0.5">{own[e.id]} own fee{own[e.id] === 1 ? '' : 's'}</span>
                      : <span className="text-[11px] font-bold text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">No fees yet</span>}
                  </label>
                ))}
              </div>
            </Field>
            {conflicts > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-semibold text-amber-800" data-testid="apply-conflict-note">{conflicts} of the selected league{conflicts === 1 ? ' already has' : 's already have'} fees. What should happen?</p>
                <ModePicker mode={mode} setMode={setMode} />
              </div>
            )}
            {t && t.budget_minor !== null && (
              <label className="flex items-start gap-2.5 text-sm text-slate-700">
                <Checkbox checked={withBudget} onCheckedChange={v => setWithBudget(!!v)} className="mt-0.5" data-testid="apply-include-budget" />
                <span>Also set the budget to {fmtMoney(t.budget_minor, currency)}{effMode === 'fill' ? ' (only where no budget is set)' : ''}</span>
              </label>
            )}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          {templates.length > 0 && (
            <Button disabled={busy || !t || !sel.size || !effMode} onClick={submit} className="bg-[#00247D] hover:bg-[#001A5C]" data-testid="apply-template-submit">
              {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}Apply template
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
